import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getMe, logout as apiLogout } from '../api/api';
import { useQueryClient } from '@tanstack/react-query';
import { setAnalyticsUserId } from '../utils/analytics';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const FIRST_VISIT_KEY = 'karmana_first_visit';

export const getStoredFirstVisit = () => {
  try {
    return JSON.parse(localStorage.getItem(FIRST_VISIT_KEY) || 'null');
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => !window.location.hash?.includes('session_id='));
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTrigger, setAuthModalTrigger] = useState('default');
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const refresh = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      setAnalyticsUserId(res.data?.id || null);
      return res.data;
    } catch {
      setUser(null);
      setAnalyticsUserId(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return;
    let active = true;
    (async () => {
      try {
        const res = await getMe();
        if (active) {
          setUser(res.data);
          setAnalyticsUserId(res.data?.id || null);
          const u = res.data;
          // New-user onboarding: if the signup flag survived a page refresh and
          // onboarding hasn't been done yet, show the modal.
          if (u && !u.onboarding_done && sessionStorage.getItem('karmana_new_user_onboarding')) {
            setTimeout(() => {
              const current = userRef.current;
              if (current && !current.onboarding_done) setOnboardingOpen(true);
            }, 1500);
          }
        }
      } catch {
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const queryClient = useQueryClient();

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch { /* ignore */ }
    setUser(null);
    setAnalyticsUserId(null);
    queryClient.removeQueries({ queryKey: ['my-activities'] });
  }, [queryClient]);

  const openAuthModal  = useCallback((trigger = 'default') => { setAuthModalTrigger(trigger); setAuthModalOpen(true); },  []);
  const closeAuthModal = useCallback(() => { setAuthModalOpen(false); setAuthModalTrigger('default'); }, []);

  const requireAuth = useCallback(
    (action, trigger = 'default') => { if (user) { action(); } else { setAuthModalTrigger(trigger); setAuthModalOpen(true); } },
    [user]
  );

  /* ── Onboarding modal ── */
  const ONBOARDING_PENDING_KEY = 'karmana_new_user_onboarding';

  const triggerOnboarding = useCallback((delayMs = 4000, force = false) => {
    setTimeout(() => {
      const u = userRef.current;
      if (u?.onboarding_done) return;
      if (!force && !sessionStorage.getItem(ONBOARDING_PENDING_KEY)) return;
      setOnboardingOpen(true);
    }, delayMs);
  }, []);

  const closeOnboarding = useCallback(async (completed) => {
    setOnboardingOpen(false);
    try { sessionStorage.removeItem(ONBOARDING_PENDING_KEY); } catch { /* ignore */ }
    if (completed) await refresh();
  }, [refresh]);

  const value = {
    user,
    loading,
    refresh,
    setUser,
    logout,
    authModalOpen,
    authModalTrigger,
    openAuthModal,
    closeAuthModal,
    requireAuth,
    // Onboarding
    onboardingOpen,
    triggerOnboarding,
    closeOnboarding,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
